import React, { useState, useEffect, useRef } from 'react';
import './Overlay.css';

const PRE_RECORDED_NAMES = new Set([
  'thenomadandy', 'FollowAlwaysDisneyWorld', 'WorldScape', 'GeoffreyNease', 'WDW247', 'goodenjoymenttv',
]);

const MOST_POPULAR_NAMES = new Set([
  'ResortTV1', 'PagingMrMorrow', 'WDWNT', 'WDWNTTV',
  'TheTrackers', 'DailyFastPass', 'CrazyDisneyLady', 'ParkPassTV',
]);

const VideoOverlay = ({ onSelect, onClose, liveStreams = {}, currentVideoIds = [] }) => {
  const [selectedStreamer, setSelectedStreamer] = useState('');
  const [flatLiveList, setFlatLiveList] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [sectionOffsets, setSectionOffsets] = useState({});
  const itemRefs = useRef({});

  const isInUse = (videoId) => currentVideoIds.includes(videoId);
  const streamerList = Object.keys(liveStreams);

  const mostPopular = [];
  const liveInParks = [];
  const liveLoops = [];

  const BLOCKED_VIDEO_IDS = new Set(['q6mmNbQ-krs']);

  for (const streamer of streamerList) {
    const stream = liveStreams[streamer];
    if (!stream.live || BLOCKED_VIDEO_IDS.has(stream.live.videoId)) continue;

    const videoData = {
      streamer,
      videoId: stream.live.videoId,
      thumbnail: stream.live.thumbnail,
      title: stream.live.title,
    };

    if (MOST_POPULAR_NAMES.has(streamer)) {
      mostPopular.push(videoData);
    } else if (PRE_RECORDED_NAMES.has(streamer)) {
      liveLoops.push(videoData);
    } else {
      liveInParks.push(videoData);
    }
  }

  useEffect(() => {
    const all = [...mostPopular, ...liveInParks, ...liveLoops];
    all.push({ type: 'cancel' });
    setFlatLiveList(all);
    setHighlightIndex(0);
    setSectionOffsets({
      mostPopular: 0,
      liveInParks: mostPopular.length,
      liveLoops: mostPopular.length + liveInParks.length,
    });
  }, [liveStreams]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!flatLiveList.length) return;

      const itemsPerRow = 3;
      let newIndex = highlightIndex;

      const getRowAndCol = (index, sectionStart) => {
        const localIndex = index - sectionStart;
        return {
          row: Math.floor(localIndex / itemsPerRow),
          col: localIndex % itemsPerRow,
        };
      };

      const sections = [
        { list: mostPopular, offset: sectionOffsets.mostPopular },
        { list: liveInParks, offset: sectionOffsets.liveInParks },
        { list: liveLoops, offset: sectionOffsets.liveLoops },
      ];

      const isCancelSelected = flatLiveList[highlightIndex]?.type === 'cancel';

      switch (e.key) {
        case 'ArrowRight':
          if (!isCancelSelected) newIndex = (highlightIndex + 1) % flatLiveList.length;
          break;
        case 'ArrowLeft':
          if (!isCancelSelected) newIndex = (highlightIndex - 1 + flatLiveList.length) % flatLiveList.length;
          break;
        case 'ArrowDown': {
          if (isCancelSelected) break;
          const currentSection = sections.find(({ offset, list }) => highlightIndex >= offset && highlightIndex < offset + list.length);
          if (!currentSection) break;
          const { row, col } = getRowAndCol(highlightIndex, currentSection.offset);
          const nextSectionIndex = sections.findIndex(s => s.offset === currentSection.offset) + 1;
          if (nextSectionIndex < sections.length) {
            const next = sections[nextSectionIndex];
            const targetIndex = next.offset + col;
            newIndex = Math.min(targetIndex, next.offset + next.list.length - 1);
          } else {
            newIndex = flatLiveList.length - 1;
          }
          break;
        }
        case 'ArrowUp': {
          if (isCancelSelected) {
            const lastOffset = sectionOffsets.liveLoops;
            const lastCol = (flatLiveList.length - 2) % itemsPerRow;
            newIndex = lastOffset + lastCol;
            break;
          }
          const currentSection = sections.find(({ offset, list }) => highlightIndex >= offset && highlightIndex < offset + list.length);
          if (!currentSection) break;
          const { row, col } = getRowAndCol(highlightIndex, currentSection.offset);
          const prevSectionIndex = sections.findIndex(s => s.offset === currentSection.offset) - 1;
          if (prevSectionIndex >= 0) {
            const prev = sections[prevSectionIndex];
            const targetIndex = prev.offset + col;
            newIndex = Math.min(targetIndex, prev.offset + prev.list.length - 1);
          }
          break;
        }
        case 'Enter': {
          const item = flatLiveList[highlightIndex];
          if (item?.type === 'cancel') {
            onClose();
          } else if (!isInUse(item?.videoId)) {
            onSelect(item.videoId);
          }
          break;
        }
        default: return;
      }

      setHighlightIndex(newIndex);

      const item = flatLiveList[newIndex];
      const ref = item?.type === 'cancel' ? itemRefs.current.cancel : itemRefs.current[item.videoId];
      if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      e.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [highlightIndex, flatLiveList, sectionOffsets]);

  const renderSection = (title, list) => (
    <>
      <h3>{title}</h3>
      <div className="thumbnail-grid">
        {list.map((data) => {
          const disabled = isInUse(data.videoId);
          const isSelected = flatLiveList[highlightIndex]?.videoId === data.videoId;

          return (
            <div
              key={data.videoId}
              ref={(el) => (itemRefs.current[data.videoId] = el)}
              className={`thumbnail-item ${isSelected ? 'overlay-selected' : ''}`}
              onMouseEnter={() => setHighlightIndex(flatLiveList.findIndex(v => v.videoId === data.videoId))}
            >
              <img
                src={data.thumbnail}
                alt={data.title}
                className={disabled ? 'disabled' : ''}
                onClick={() => !disabled && onSelect(data.videoId)}
              />
              <div className={`thumbnail-title ${disabled ? 'disabled' : ''}`}>
                <strong>@{data.streamer}</strong><br />
                {disabled ? ' (Currently Playing)' : ''}
              </div>
              {isSelected && (
                <div className="stream-title-inline">
                  <strong>{data.title}</strong>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <hr />
    </>
  );

  return (
    <div className="overlay">
      <div className="overlay-content">
        <h2>Choose a Streamer</h2>
        {mostPopular.length > 0 && renderSection('⭐ Most Popular', mostPopular)}
        {liveInParks.length > 0 && renderSection('🎢 LIVE IN THE PARKS!', liveInParks)}
        {liveLoops.length > 0 && renderSection('🔁 Pre-recorded 24/7', liveLoops)}

        <h3 style={{ marginBottom: '-1rem' }}>📼 Archived Streams</h3>
        <select value={selectedStreamer} onChange={(e) => setSelectedStreamer(e.target.value)}>
          <option value="">-- Select a Streamer --</option>
          {streamerList.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {selectedStreamer && liveStreams[selectedStreamer]?.recent?.length > 0 && (
          <div className="streamer-block">
            <h3>Recent Videos:</h3>
            {liveStreams[selectedStreamer].recent.map((video) => {
              const disabled = isInUse(video.videoId);
              return (
                <button
                  key={video.videoId}
                  onClick={() => !disabled && onSelect(video.videoId)}
                  disabled={disabled}
                  className={disabled ? 'disabled' : ''}
                >
                  {video.title}
                  {disabled ? ' (Already Playing)' : ''}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            className={`close-btn ${flatLiveList[highlightIndex]?.type === 'cancel' ? 'overlay-selected' : ''}`}
            ref={(el) => (itemRefs.current.cancel = el)}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoOverlay;